import { isEmpty, isNull, max } from 'lodash'
import { createRoutingProjectPlanCollection } from 'features/RoutingProjects/collections'
import {
  IRoutingProjectDataset,
  IRoutingProjectDatasetLocation,
  IRoutingProjectModel,
  IRoutingProjectPlan,
  IRoutingProjectPlanModel,
  IRoutingProjectPlanRouteStop,
  IRoutingProjectPlanSkippedLocation
} from 'features/RoutingProjects/types'
import {
  RoutingProjectDatasetFragment,
  RoutingProjectFragment,
  RoutingProjectPlanFragment,
  RoutingProjectPlanStatus
} from 'gql/generated/graphql'
import {
  encodeRouteShape,
  getGoogleDirectionsRoute,
  getTotalDistanceByRoute,
  getTotalDurationByRoute
} from 'utils/google-maps'
import { secondsToMinutes } from 'utils/number'
import { subtractMinutesWithDayShift } from 'utils/time'
import { getUniqueColor } from 'utils/style'

export const isLocationSkippedByPlanOptimization = ({
  skippedLocations,
  locationId
}: {
  skippedLocations: IRoutingProjectPlanSkippedLocation[]
  locationId: string
}): boolean => {
  return skippedLocations.some((skippedLocation) => skippedLocation.location.id === locationId)
}

const MINIMUM_STOPS_BY_ROUTE = 2

export const moveLocationFromSkippedLocationsToRoute = ({
  plan,
  locationId,
  droppedRouteIndex,
  droppedStopIndex
}: {
  plan: IRoutingProjectPlan
  locationId: string
  droppedRouteIndex: number | null
  droppedStopIndex: number
}): IRoutingProjectPlan => {
  const newSkippedLocations = plan.optimization.skippedLocations.filter(
    (skippedLocation) => skippedLocation.location.id !== locationId
  )
  const locationToMove = plan.optimization.skippedLocations.find(
    (skippedLocation) => skippedLocation.location.id === locationId
  )
  if (!locationToMove) {
    return {
      ...plan
    }
  }
  const newRoutes = plan.optimization.routes.map((route, routeIndex) => {
    if (routeIndex === droppedRouteIndex) {
      const newStops = [...route.stops]
      newStops.splice(droppedStopIndex, 0, {
        sortableItemId: locationToMove.location.id,
        departure: null,
        arrival: null,
        location: {
          ...locationToMove.location,
          wasSkipped: true,
          skippedReasons: locationToMove.reasons
        }
      })
      return {
        ...route,
        stops: newStops
      }
    }
    return route
  })

  return {
    ...plan,
    optimization: {
      ...plan.optimization,
      skippedLocations: newSkippedLocations,
      routes: newRoutes
    }
  }
}

export const moveLocationFromRouteToSkippedLocations = ({
  plan,
  locationId,
  previousRouteIndex
}: {
  plan: IRoutingProjectPlan
  locationId: string
  previousRouteIndex: number
}): IRoutingProjectPlan => {
  const previousRoute = plan.optimization.routes[previousRouteIndex]

  if (previousRoute.stops.length <= MINIMUM_STOPS_BY_ROUTE) {
    return {
      ...plan
    }
  }

  const stopToMove = previousRoute.stops.find((stop) => stop.location.id === locationId)

  if (!stopToMove?.location.wasSkipped) {
    return {
      ...plan
    }
  }

  const newRoutes = plan.optimization.routes.map((route, routeIndex) => {
    if (routeIndex === previousRouteIndex) {
      // Remove location from previous route
      const newStops = route.stops.filter((stop) => stop.location.id !== locationId)
      return {
        ...route,
        stops: newStops
      }
    }
    return route
  })
  if (!stopToMove) {
    return {
      ...plan
    }
  }
  const newSkippedLocations = plan.optimization.skippedLocations
  newSkippedLocations.push({
    location: stopToMove.location,
    reasons: stopToMove.location.skippedReasons || []
  })

  return {
    ...plan,
    optimization: {
      ...plan.optimization,
      skippedLocations: newSkippedLocations,
      routes: newRoutes
    }
  }
}

export const moveLocationFromOneRouteToAnother = ({
  plan,
  locationId,
  previousRouteIndex,
  droppedRouteIndex
}: {
  plan: IRoutingProjectPlan
  locationId: string
  previousRouteIndex: number
  droppedRouteIndex: number
}): IRoutingProjectPlan => {
  const previousRoute = plan.optimization.routes[previousRouteIndex]

  if (previousRoute.stops.length <= MINIMUM_STOPS_BY_ROUTE) {
    return {
      ...plan
    }
  }
  const routeContainsLocation = plan.optimization.routes[droppedRouteIndex].stops.some(
    (stop) => stop.location.id === locationId
  )
  if (routeContainsLocation) {
    return {
      ...plan
    }
  }
  const newRoutes = plan.optimization.routes.map((route, routeIndex) => {
    if (routeIndex === previousRouteIndex) {
      // Remove location from previous route
      const newStops = route.stops.filter((stop) => stop.location.id !== locationId)
      return {
        ...route,
        stops: newStops
      }
    }
    if (routeIndex === droppedRouteIndex) {
      const newStops = [...route.stops]
      const stopToMove = plan.optimization.routes[previousRouteIndex].stops.find(
        (stop) => stop.location.id === locationId
      )
      if (!stopToMove) {
        return route
      }
      newStops.push({
        ...stopToMove
      })
      return {
        ...route,
        stops: newStops
      }
    }
    return route
  })

  return {
    ...plan,
    optimization: {
      ...plan.optimization,
      routes: newRoutes
    }
  }
}

export const moveLocationInsidePlanOptimizationBetweenContainers = ({
  plan,
  locationId,
  previousRouteIndex,
  droppedLocationId,
  droppedRouteIndex
}: {
  plan: IRoutingProjectPlan
  locationId: string
  previousRouteIndex: number | null
  droppedLocationId: string
  droppedRouteIndex: number | null
}): IRoutingProjectPlan => {
  if (previousRouteIndex === droppedRouteIndex) {
    return {
      ...plan
    }
  }
  const droppedLocationIndex = getLocationIndexByOptimizationAndLocationId({
    optimization: plan.optimization,
    locationId: droppedLocationId,
    routeIndex: droppedRouteIndex
  })
  if (isNull(droppedLocationIndex)) {
    return {
      ...plan
    }
  }
  if (isNull(previousRouteIndex)) {
    return moveLocationFromSkippedLocationsToRoute({
      plan,
      locationId,
      droppedRouteIndex,
      droppedStopIndex: droppedLocationIndex
    })
  } else {
    if (isNull(droppedRouteIndex)) {
      return moveLocationFromRouteToSkippedLocations({
        plan,
        locationId,
        previousRouteIndex
      })
    } else {
      return moveLocationFromOneRouteToAnother({
        plan,
        locationId,
        previousRouteIndex,
        droppedRouteIndex
      })
    }
  }
}

export const moveSkippedLocationInsideSkippedLocations = ({
  plan,
  locationId,
  droppedLocationId
}: {
  plan: IRoutingProjectPlan
  locationId: string
  droppedLocationId: string
}): IRoutingProjectPlan => {
  let newSkippedLocations = [...plan.optimization.skippedLocations]
  const locationToMove = plan.optimization.skippedLocations.find(
    (skippedLocation) => skippedLocation.location.id === locationId
  )
  if (!locationToMove) {
    return {
      ...plan
    }
  }
  const droppedLocationIndex = newSkippedLocations.findIndex(
    (skippedLocation) => skippedLocation.location.id === droppedLocationId
  )
  if (droppedLocationIndex === -1) {
    return {
      ...plan
    }
  }
  newSkippedLocations = plan.optimization.skippedLocations.filter(
    (skippedLocation) => skippedLocation.location.id !== locationId
  )
  newSkippedLocations.splice(droppedLocationIndex, 0, locationToMove)
  return {
    ...plan,
    optimization: {
      ...plan.optimization,
      skippedLocations: newSkippedLocations
    }
  }
}

export const moveLocationInsideSameRoute = ({
  plan,
  locationId,
  droppedLocationId,
  routeIndex
}: {
  plan: IRoutingProjectPlan
  locationId: string
  droppedLocationId: string
  routeIndex: number
}): IRoutingProjectPlan => {
  const newRoutes = [...plan.optimization.routes]
  const route = newRoutes[routeIndex]
  const stopToMove = route.stops.find((stop) => stop.location.id === locationId)
  if (!stopToMove) {
    return {
      ...plan
    }
  }
  const droppedStopIndex = route.stops.findIndex((stop) => stop.location.id === droppedLocationId)
  const newStops = route.stops.filter((stop) => stop.location.id !== locationId)
  newStops.splice(droppedStopIndex, 0, {
    ...stopToMove
  })
  newRoutes[routeIndex] = {
    ...route,
    stops: newStops
  }
  return {
    ...plan,
    optimization: {
      ...plan.optimization,
      routes: newRoutes
    }
  }
}

export const moveLocationInsidePlanOptimizationInsideSameContainer = (
  plan: IRoutingProjectPlan,
  locationId: string,
  droppedLocationId: string,
  routeIndex: number | null
): IRoutingProjectPlan => {
  if (isNull(routeIndex)) {
    return moveSkippedLocationInsideSkippedLocations({ plan, locationId, droppedLocationId })
  } else {
    return moveLocationInsideSameRoute({ plan, locationId, droppedLocationId, routeIndex })
  }
}

export const getLocationIndexByOptimizationAndLocationId = ({
  optimization,
  locationId,
  routeIndex
}: {
  optimization: IRoutingProjectPlan['optimization']
  locationId: string
  routeIndex: number | null
}): number | null => {
  let locationIndex
  if (isNull(routeIndex)) {
    locationIndex = optimization.skippedLocations.findIndex(
      (skippedLocation) => skippedLocation.location.id === locationId
    )
  } else {
    const route = optimization.routes[routeIndex]
    locationIndex = route.stops.findIndex((stop) => stop.location.id === locationId)
  }

  return locationIndex === -1 ? null : locationIndex
}

export const removeLocationFromPlan = ({
  plan,
  locationId
}: {
  plan: IRoutingProjectPlan
  locationId: string
}): IRoutingProjectPlan => {
  const newPlan: IRoutingProjectPlan = {
    ...plan,
    dataset: {
      ...(plan.dataset as IRoutingProjectDataset),
      locations: plan.dataset?.locations?.filter((location) => location.id !== locationId) || []
    },
    optimization: {
      ...plan.optimization,
      routes: plan.optimization?.routes.map((route) => {
        return {
          ...route,
          stops: route.stops.filter((stop) => stop.location.id !== locationId)
        }
      })
    }
  }

  return newPlan
}

export const addRoutingProjectPlanRouteStop = ({
  plan,
  routeIndex,
  location
}: {
  plan: IRoutingProjectPlan
  routeIndex: number
  location: IRoutingProjectDatasetLocation
}): IRoutingProjectPlan => {
  return {
    ...plan,
    dataset: {
      ...(plan.dataset as IRoutingProjectDataset),
      locations: [...(plan.dataset?.locations || []), location]
    },
    optimization: {
      ...plan.optimization,
      routes: plan.optimization?.routes.map((route, index) => {
        if (index === routeIndex) {
          return {
            ...route,
            stops: [
              ...route.stops,
              {
                location: location,
                sortableItemId: location.id
              }
            ]
          }
        }
        return route
      })
    }
  } as IRoutingProjectPlan
}

export const updateLocationCoordinates = (
  plan: IRoutingProjectPlan,
  locationId: string,
  coordinates: { latitude: number; longitude: number }
): IRoutingProjectPlan => {
  const newDatasetLocations =
    plan.dataset?.locations?.map((location) => {
      if (location.id === locationId) {
        return {
          ...location,
          coordinates
        }
      }
      return location
    }) || []
  const newSkippedLocations = plan.optimization?.skippedLocations.map((skippedLocation) => {
    if (skippedLocation.location.id === locationId) {
      return {
        ...skippedLocation,
        location: {
          ...skippedLocation.location,
          coordinates
        }
      }
    }
    return skippedLocation
  })
  const newRoutes = plan.optimization?.routes.map((route) => {
    return {
      ...route,
      stops: route.stops.map((stop) => {
        if (locationId === stop.location.id) {
          return {
            ...stop,
            location: {
              ...stop.location,
              coordinates
            }
          }
        }
        return stop
      })
    }
  })
  const newPlan: IRoutingProjectPlan = {
    ...plan,
    dataset: plan.dataset
      ? {
          ...(plan.dataset as IRoutingProjectDataset),
          locations: newDatasetLocations
        }
      : undefined,
    optimization: {
      ...plan.optimization,
      skippedLocations: newSkippedLocations,
      routes: newRoutes
    }
  }
  return newPlan
}

const DEFAULT_START_TIME = '00:00'

/**
 * Get the start time based on the constraints
 * @param constraints - constraints of the plan
 * @param totalRouteDuration - total duration of the route in minutes
 *
 * @returns start time in format HH:mm
 */
export const getStopsStartTime = ({
  startTimeConstraint,
  endTimeConstraint,
  totalRouteDuration
}: {
  startTimeConstraint?: string | null
  endTimeConstraint?: string | null
  totalRouteDuration: number
}): string => {
  if (startTimeConstraint) {
    return startTimeConstraint
  } else if (endTimeConstraint) {
    return subtractMinutesWithDayShift(endTimeConstraint, totalRouteDuration).updatedTime
  }
  return DEFAULT_START_TIME
}

/**
 * Update stops itinerary based on the legs duration and the stop duration constraint
 * @param stops
 * @param legs
 * @param startTime
 * @param stopDuration - stop duration constraint in minutes
 *
 * @returns updated stops itinerary
 */
export const updateStopsItinerary = ({
  stops,
  legs,
  startTime,
  stopDuration = 0
}: {
  stops: IRoutingProjectPlanRouteStop[]
  legs: google.maps.DirectionsLeg[]
  startTime: string
  stopDuration?: number
}): IRoutingProjectPlanRouteStop[] => {
  let nextArrival = {
    time: startTime,
    day: 0
  }
  return stops.map((stop, stopIndex) => {
    const newArrival = nextArrival
    const newDeparture = subtractMinutesWithDayShift(newArrival.time, -stopDuration)
    const newDepartureTime = newDeparture.updatedTime
    const newDepartureDay = newDeparture.dayDifference + newArrival.day
    const legDuration = secondsToMinutes(legs.at(stopIndex)?.duration?.value || 0)
    const nextStopArrival = subtractMinutesWithDayShift(newDeparture.updatedTime, -legDuration)
    nextArrival = {
      time: nextStopArrival.updatedTime,
      day: nextStopArrival.dayDifference + newDeparture.dayDifference
    }
    const isDestination = stopIndex === stops.length - 1
    return {
      ...stop,
      arrival: newArrival,
      departure: !isDestination
        ? {
            time: newDepartureTime,
            day: newDepartureDay
          }
        : null
    }
  })
}

export const updateRoutesPolyline = async (
  planModel: IRoutingProjectPlanModel,
  updatedRoutesIndex: number[]
): Promise<IRoutingProjectPlanModel> => {
  if (isEmpty(updatedRoutesIndex)) {
    return planModel
  }
  const promises = updatedRoutesIndex.map((routeIndex) => {
    const route = planModel.optimization?.routes[routeIndex]
    if (!route) {
      return
    }
    return getGoogleDirectionsRoute(
      route.stops.map(({ location }) => {
        return {
          id: location.id,
          name: location.label,
          position: {
            lat: location.coordinates.latitude,
            lng: location.coordinates.longitude
          }
        }
      })
    )
  })
  const results = await Promise.allSettled(promises)
  const stopDurationConstraint = planModel.constraints.stopDuration || 0
  const newOptimizedRoutes = planModel.optimization.routes.map((route, index) => {
    const resultsIndex = updatedRoutesIndex.indexOf(index)
    if (resultsIndex > -1) {
      const directionsRoute =
        results[resultsIndex].status === 'fulfilled'
          ? (results[resultsIndex] as PromiseFulfilledResult<google.maps.DirectionsRoute>).value
          : null

      if (directionsRoute) {
        const extraDuration = stopDurationConstraint * (max([route.stops.length - 1, 0]) as number)
        const totalDuration = secondsToMinutes(getTotalDurationByRoute(directionsRoute)) + extraDuration
        const startTime = getStopsStartTime({
          startTimeConstraint: planModel.constraints.startTime,
          endTimeConstraint: planModel.constraints.endTime,
          totalRouteDuration: totalDuration
        })
        const updatedStops = updateStopsItinerary({
          stops: route.stops,
          legs: directionsRoute.legs,
          startTime,
          stopDuration: stopDurationConstraint
        })
        return {
          ...route,
          polyline: encodeRouteShape(directionsRoute),
          decodedPolyline: directionsRoute.overview_path.map((point) => {
            return {
              lat: point.lat(),
              lng: point.lng()
            }
          }),
          distance: getTotalDistanceByRoute(directionsRoute),
          duration: totalDuration,
          stops: updatedStops
        }
      }
    }
    return route
  })
  return {
    ...planModel,
    optimization: {
      ...planModel.optimization,
      routes: newOptimizedRoutes
    }
  }
}

export const createRoutingProjectPlanModel = (
  data?: RoutingProjectPlanFragment,
  geometryLib?: google.maps.GeometryLibrary | null
): IRoutingProjectPlanModel => {
  const dataset = data?.dataset as RoutingProjectDatasetFragment | undefined
  const baseModel: IRoutingProjectPlan = {
    id: data?.id || '',
    name: data?.name || '',
    status: data?.status || RoutingProjectPlanStatus.Simulation,
    dataset: {
      name: dataset?.name || '',
      locations:
        dataset?.locations?.map((location) => ({
          id: location.id,
          label: location.label,
          coordinates: {
            latitude: parseFloat(location.coordinates.latitude),
            longitude: parseFloat(location.coordinates.longitude)
          }
        })) || []
    },
    constraints: {
      origin: data?.constraints?.origin
        ? {
            id: data.constraints.origin.id,
            label: data.constraints.origin.label
          }
        : null,
      destination: data?.constraints?.destination
        ? {
            id: data.constraints.destination.id,
            label: data.constraints.destination.label
          }
        : null,
      vehicles:
        data?.constraints?.vehicles?.map((vehicle) => ({
          number: vehicle.number,
          capacity: vehicle.capacity
        })) || [],
      maxStops: data?.constraints?.maxStops || null,
      maxDuration: data?.constraints?.maxDuration || null,
      maxDistance: data?.constraints?.maxDistance || null,
      stopDuration: data?.constraints?.stopDuration || null,
      startTime: data?.constraints?.startTime || null,
      endTime: data?.constraints?.endTime || null
    },
    optimization: {
      routes:
        data?.optimization?.routes?.map((route, routeIndex) => ({
          id: route.id,
          polyline: route.polyline,
          decodedPolyline: geometryLib
            ? geometryLib.encoding.decodePath(route.polyline || '').map((point) => ({
                lat: point.lat(),
                lng: point.lng()
              }))
            : [],
          distance: route.distance,
          duration: route.duration,
          stops: route.stops.map((stop) => ({
            sortableItemId: `${stop.location.id}-${routeIndex}`,
            departure: stop.departure?.time ? { day: stop.departure.day, time: stop.departure.time } : null,
            arrival: stop.arrival?.time ? { day: stop.arrival.day, time: stop.arrival.time } : null,
            location: {
              id: stop.location.id,
              label: stop.location.label,
              coordinates: {
                latitude: parseFloat(stop.location.coordinates.latitude),
                longitude: parseFloat(stop.location.coordinates.longitude)
              }
            }
          })),
          rgbColor: getUniqueColor(routeIndex)
        })) || [],
      skippedLocations:
        data?.optimization?.skippedLocations?.map((skippedLocation) => ({
          reasons: skippedLocation.reasons,
          location: {
            id: skippedLocation.location.id,
            label: skippedLocation.location.label,
            coordinates: {
              latitude: parseFloat(skippedLocation.location.coordinates.latitude),
              longitude: parseFloat(skippedLocation.location.coordinates.longitude)
            }
          }
        })) || []
    }
  }

  const isSkippedLocation = (locationId: string): boolean => {
    return isLocationSkippedByPlanOptimization({
      skippedLocations: baseModel.optimization.skippedLocations,
      locationId
    })
  }

  const getDatasetLocation = (locationId: string): IRoutingProjectDatasetLocation | null => {
    return baseModel.dataset?.locations?.find((location) => location.id === locationId) || null
  }

  return {
    ...baseModel,
    isSkippedLocation,
    getDatasetLocation
  }
}

export const createRoutingProjectModel = (data?: RoutingProjectFragment): IRoutingProjectModel => {
  return {
    id: data?.id || '',
    name: data?.name || '',
    tags: data?.tags || [],
    plans: createRoutingProjectPlanCollection((data?.plans as RoutingProjectPlanFragment[]) || []),
    datasets:
      (data?.datasets as RoutingProjectDatasetFragment[])?.map((dataset) => ({
        id: dataset.id,
        name: dataset.name || '',
        locations:
          dataset.locations?.map((location) => ({
            id: location.id,
            label: location.label,
            coordinates: {
              latitude: parseFloat(location.coordinates.latitude),
              longitude: parseFloat(location.coordinates.longitude)
            }
          })) || []
      })) || []
  }
}
